import { linkWorkoutItems } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ButtonGroup,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "ui";

import { InsertButton } from "src/components/LinkWorkoutItemsInsertButton";
import { useMinimapDragAndDropManager } from "src/components/MinimapDragAndDropManager";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { type LinkButtonGroupProps } from "src/workouts/LinkUnlinkWorkoutItemsButton";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { useWorkoutItemsDragAndDropManager } from "src/workouts/WorkoutItemsDragAndDropManagerContext";

export const LinkButtonGroup = observer(function LinkButtonGroup(
  props: LinkButtonGroupProps,
) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const manager = useWorkoutItemsDragAndDropManager();
  const minimapManager = useMinimapDragAndDropManager();
  const { startSaving } = useAuthenticatedNavigationContext();
  const [isLoading, setIsLoading] = useState(false);

  const canLink = workoutEditStore.canMergeWithNext(props.itemId);
  const nextSiblingId = workoutEditStore.getNextSiblingId(props.itemId);

  if (!canLink || !nextSiblingId) {
    return (
      <ButtonGroup>
        <InsertButton
          onInsert={props.onInsert}
          sections={props.sections}
          onAddSection={props.onAddSection}
          onImportFromLibrary={props.onImportFromLibrary}
        />
      </ButtonGroup>
    );
  }

  async function handleLink() {
    if (!nextSiblingId) {
      return;
    }

    setIsLoading(true);
    const stop = startSaving();

    try {
      const response = await linkWorkoutItems({
        path: { workoutId: workoutEditStore.workout.id },
        body: { itemId: props.itemId, withItemId: nextSiblingId },
      });

      const data = response.data.data;

      const newGroupIds = data.operations.flatMap((op) => {
        return op.operation === "create" && op.data.type === "GROUP"
          ? [op.data.id]
          : [];
      });
      manager.openAccordionItems(newGroupIds);

      for (const id of newGroupIds) {
        minimapManager.expandItem(id);
      }

      workoutEditStore.applyWorkoutItemChanges(data.operations);

      stop();
    } catch {
      stop("error");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ButtonGroup>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={isLoading}
            onClick={handleLink}
          >
            <Icon name="tabler-link" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("editWorkout.link.tooltip")}</TooltipContent>
      </Tooltip>
      <InsertButton
        onInsert={props.onInsert}
        sections={props.sections}
        onAddSection={props.onAddSection}
        onImportFromLibrary={props.onImportFromLibrary}
      />
    </ButtonGroup>
  );
});
